/**
 * ImageLightbox — o visualizador de imagens da página pública do orçamento.
 *
 * ─── POR QUE ELE NÃO É UM DIÁLOGO NORMAL ────────────────────────────────────
 *
 * A versão anterior era um `DialogContent` de `max-w-[95vw]` com a imagem num
 * `max-h-[80vh]` e mais `p-8` de respiro: no celular sobrava um retângulo preto
 * enorme em volta de uma foto pequena. O zoom era por BOTÃO (`+`/`−` com um
 * rótulo "100%"), que é o gesto errado num aparelho de toque — o cliente abre a
 * foto de um item para conferir um detalhe e espera a foto ocupar a tela e
 * responder aos dedos, como na galeria do próprio Android.
 *
 * Agora ele é tela cheia (`100dvw × 100dvh`, sem cantos nem padding) e a imagem
 * usa a largura toda, preservando a proporção (`object-contain`).
 *
 * ─── POR QUE O ZOOM É IMPLEMENTADO À MÃO ────────────────────────────────────
 *
 * O zoom nativo do navegador é do DOCUMENTO: dentro de um overlay `fixed` ele
 * amplia a página inteira por baixo do visualizador, com o resultado de que o
 * conteúdo do orçamento fica gigante atrás da foto e a tela não volta sozinha ao
 * fechar. Por isso o gesto é tratado aqui, com Pointer Events (que unificam
 * toque, caneta e mouse) e `touch-action: none` no palco — sem essa propriedade
 * o WebView "rouba" o segundo dedo para rolar a página e o pinch nunca chega ao
 * handler.
 *
 * O que o palco entende: PINÇA para ampliar (1× a 5×), ARRASTAR com um dedo para
 * mover quando já está ampliado, e TOQUE DUPLO para alternar entre 1× e 2,5×.
 * Com a imagem em 1× o arraste horizontal TROCA de foto — e só nesse estado,
 * senão mover a foto ampliada pularia para a próxima sem querer.
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { ChevronLeft, ChevronRight, X } from 'lucide-react';
import { VisuallyHidden } from '@radix-ui/react-visually-hidden';

interface LightboxImage {
  url: string;
  title?: string;
  description?: string;
}

interface ImageLightboxProps {
  images: LightboxImage[];
  initialIndex?: number;
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const MIN_SCALE = 1;
const MAX_SCALE = 5;
/** Ampliação do toque duplo — o suficiente para ler uma etiqueta, sem perder o enquadramento. */
const DOUBLE_TAP_SCALE = 2.5;
/** Percurso horizontal que conta como "trocar de foto" (em px, na tela). */
const SWIPE_THRESHOLD = 60;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

export function ImageLightbox({
  images,
  initialIndex = 0,
  open,
  onOpenChange,
}: ImageLightboxProps) {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });

  /** Ponteiros ativos no palco, por id. Dois = pinça; um = arraste. */
  const pointers = useRef(new Map<number, { x: number; y: number }>());
  /** Estado no instante em que a pinça começou, para o gesto ser proporcional. */
  const pinchStart = useRef<{ distance: number; scale: number } | null>(null);
  const dragStart = useRef<{ x: number; y: number; offsetX: number; offsetY: number } | null>(null);
  const lastTap = useRef(0);
  /**
   * A ampliação corrente, em REF.
   *
   * Os handlers de ponteiro precisam do valor ATUAL, e ler o `scale` do estado
   * dentro deles lê o valor do render em que o handler foi criado. Medido no
   * browser: depois de uma pinça que levou a 3×, o toque duplo lia `scale === 1`
   * e AMPLIAVA em vez de voltar ao normal — o gesto passava a fazer o contrário
   * do que o usuário pediu. O estado continua existindo (é ele que desenha); o
   * ref é só a leitura confiável de dentro do gesto.
   */
  const scaleRef = useRef(1);

  const currentImage = images[currentIndex];
  const hasMultiple = images.length > 1;

  /** Único caminho para mudar a ampliação — mantém o ref e o estado juntos. */
  const applyScale = useCallback((next: number) => {
    scaleRef.current = next;
    setScale(next);
  }, []);

  const resetView = useCallback(() => {
    applyScale(1);
    setOffset({ x: 0, y: 0 });
    pinchStart.current = null;
    dragStart.current = null;
  }, [applyScale]);

  const goToPrevious = useCallback(() => {
    setCurrentIndex((prev) => (prev === 0 ? images.length - 1 : prev - 1));
    resetView();
  }, [images.length, resetView]);

  const goToNext = useCallback(() => {
    setCurrentIndex((prev) => (prev === images.length - 1 ? 0 : prev + 1));
    resetView();
  }, [images.length, resetView]);

  // Setas do teclado (desktop). Vive num efeito, e não no `onKeyDown` do palco,
  // porque o foco depois de abrir fica no botão de fechar do Radix.
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => {
      if (!hasMultiple) return;
      if (event.key === 'ArrowLeft') goToPrevious();
      if (event.key === 'ArrowRight') goToNext();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, hasMultiple, goToPrevious, goToNext]);

  const handleOpenChange = (nextOpen: boolean) => {
    if (nextOpen) {
      setCurrentIndex(initialIndex);
      resetView();
    }
    onOpenChange(nextOpen);
  };

  const distanceBetweenPointers = () => {
    const [a, b] = [...pointers.current.values()];
    return Math.hypot(a.x - b.x, a.y - b.y);
  };

  const handlePointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    // A captura mantém os `pointermove` chegando quando o dedo sai da caixa da
    // imagem — mas ela LANÇA (`NotFoundError`) para um `pointerId` que o
    // navegador não reconhece como ativo. Sem o `try`, a exceção aborta o
    // handler na PRIMEIRA linha e o gesto inteiro morre em silêncio.
    try {
      (event.currentTarget as HTMLElement).setPointerCapture?.(event.pointerId);
    } catch {
      // Segue sem captura: o gesto continua funcionando dentro do palco, que
      // ocupa a tela toda.
    }
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size === 2) {
      pinchStart.current = { distance: distanceBetweenPointers(), scale: scaleRef.current };
      dragStart.current = null;
      return;
    }

    dragStart.current = {
      x: event.clientX,
      y: event.clientY,
      offsetX: offset.x,
      offsetY: offset.y,
    };

    // Toque duplo. O limiar de 300 ms é o mesmo que os navegadores usam para o
    // `dblclick`, e aqui ele precisa ser explícito porque com `touch-action:
    // none` o navegador não sintetiza mais o evento de duplo clique.
    const now = Date.now();
    if (now - lastTap.current < 300) {
      if (scaleRef.current > MIN_SCALE) resetView();
      else applyScale(DOUBLE_TAP_SCALE);
      lastTap.current = 0;
      return;
    }
    lastTap.current = now;
  };

  const handlePointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!pointers.current.has(event.pointerId)) return;
    pointers.current.set(event.pointerId, { x: event.clientX, y: event.clientY });

    if (pointers.current.size >= 2 && pinchStart.current) {
      const ratio = distanceBetweenPointers() / (pinchStart.current.distance || 1);
      applyScale(clamp(pinchStart.current.scale * ratio, MIN_SCALE, MAX_SCALE));
      return;
    }

    if (!dragStart.current) return;
    const dx = event.clientX - dragStart.current.x;
    const dy = event.clientY - dragStart.current.y;

    // Ampliado, o arraste MOVE a foto. Em 1× não há o que mover, e o mesmo
    // gesto vira a troca de foto (resolvida no `pointerup`).
    if (scaleRef.current > MIN_SCALE) {
      setOffset({ x: dragStart.current.offsetX + dx, y: dragStart.current.offsetY + dy });
    }
  };

  const handlePointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    const start = dragStart.current;
    pointers.current.delete(event.pointerId);

    if (pointers.current.size < 2) pinchStart.current = null;

    if (start && scaleRef.current === MIN_SCALE && hasMultiple) {
      const dx = event.clientX - start.x;
      const dy = event.clientY - start.y;
      // Só conta como troca de foto o gesto claramente HORIZONTAL: sem esta
      // comparação, arrastar na diagonal para fechar trocaria a imagem.
      if (Math.abs(dx) > SWIPE_THRESHOLD && Math.abs(dx) > Math.abs(dy)) {
        if (dx > 0) goToPrevious();
        else goToNext();
      }
    }

    if (pointers.current.size === 0) dragStart.current = null;
  };

  // Roda do mouse (desktop). No toque isto não dispara — lá o gesto é a pinça.
  const handleWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    const next = clamp(scaleRef.current - event.deltaY * 0.002, MIN_SCALE, MAX_SCALE);
    applyScale(next);
    if (next === MIN_SCALE) setOffset({ x: 0, y: 0 });
  };

  if (!currentImage) return null;

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      {/*
        Tela cheia de verdade: o `DialogContent` do shadcn nasce centralizado com
        `max-w`, translate e cantos arredondados — os três são desfeitos aqui, no
        mesmo molde do carrossel de onboarding (#22). `[&>button]:hidden` esconde
        o X padrão do Radix, que ficaria pequeno e claro sobre o preto; o nosso
        tem alvo de toque maior.
      */}
      <DialogContent
        className="gap-0 overflow-hidden border-none bg-black p-0 [&>button]:hidden"
        /*
          A tela cheia vai por STYLE INLINE, e isso é deliberado. O
          `DialogContent` do shadcn nasce `left-[50%] top-[50%]
          translate-x-[-50%] translate-y-[-50%] w-[calc(100%-2rem)] max-w-lg`, e
          além disso as animações de abertura (`slide-in-from-left-1/2`) mexem no
          MESMO `transform`. Medido no browser: com as classes equivalentes
          (`left-0 top-0 w-screen translate-x-0`) o `left` vencia e o `translate`
          NÃO — o diálogo abria com o canto em `-178, -369`, ou seja, metade dele
          fora da tela. Inline vence tudo, sem depender de ordem de classe nem do
          tailwind-merge.
        */
        style={{
          left: 0,
          top: 0,
          right: 0,
          bottom: 0,
          width: 'auto',
          height: 'auto',
          maxWidth: 'none',
          transform: 'none',
          borderRadius: 0,
        }}
      >
        <VisuallyHidden>
          <DialogTitle>Visualizar imagem</DialogTitle>
        </VisuallyHidden>

        <Button
          variant="ghost"
          size="icon"
          aria-label="Fechar"
          className="absolute right-2 top-2 z-50 h-11 w-11 text-white hover:bg-white/20"
          onClick={() => onOpenChange(false)}
        >
          <X className="h-6 w-6" />
        </Button>

        {hasMultiple && (
          <>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Imagem anterior"
              className="absolute left-1 top-1/2 z-40 h-12 w-12 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={goToPrevious}
            >
              <ChevronLeft className="h-8 w-8" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              aria-label="Próxima imagem"
              className="absolute right-1 top-1/2 z-40 h-12 w-12 -translate-y-1/2 text-white hover:bg-white/20"
              onClick={goToNext}
            >
              <ChevronRight className="h-8 w-8" />
            </Button>
          </>
        )}

        {/*
          O PALCO. `touch-action: none` é o que entrega os dois dedos ao handler
          em vez de a rolagem da página — sem ele o pinch simplesmente não
          acontece no WebView do Android.
        */}
        <div
          className="flex h-full w-full items-center justify-center overflow-hidden"
          style={{ touchAction: 'none' }}
          onPointerDown={handlePointerDown}
          onPointerMove={handlePointerMove}
          onPointerUp={handlePointerUp}
          onPointerCancel={handlePointerUp}
          onWheel={handleWheel}
        >
          <img
            src={currentImage.url}
            alt={currentImage.title || 'Imagem'}
            className="h-full w-full select-none object-contain"
            style={{
              transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              // Sem transição DURANTE o gesto: animar cada quadro da pinça faz a
              // imagem "perseguir" os dedos com atraso visível.
              transition: pinchStart.current || dragStart.current ? 'none' : 'transform 150ms ease-out',
            }}
            draggable={false}
          />
        </div>

        {(currentImage.title || currentImage.description || hasMultiple) && (
          <div className="pointer-events-none absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/80 to-transparent p-4 pb-[calc(1rem+var(--safe-bottom))] text-center">
            {currentImage.title && (
              <p className="font-medium text-white">{currentImage.title}</p>
            )}
            {currentImage.description && (
              <p className="text-sm text-white/70">{currentImage.description}</p>
            )}
            {hasMultiple && (
              <p className="mt-1 text-sm text-white/60">
                {currentIndex + 1} de {images.length}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
